import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const StripeConfig = ({ isAdmin, onSave }) => {
  const [stripeConfig, setStripeConfig] = useState({
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
  });
  const [stripeSuccess, setStripeSuccess] = useState('');
  const [stripeError, setStripeError] = useState('');
  const [stripeFormValid, setStripeFormValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // New loading state for saving

  // Default Stripe config to avoid dependency issue
  const defaultStripeConfig = {
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
  };

  // Load Stripe settings from Firestore
  useEffect(() => {
    if (!isAdmin) return;

    const fetchStripeConfig = async () => {
      try {
        const stripeDocRef = doc(db, 'stripeSettings', 'config');
        const stripeDoc = await getDoc(stripeDocRef);
        if (stripeDoc.exists()) {
          setStripeConfig(stripeDoc.data());
        } else {
          await setDoc(stripeDocRef, defaultStripeConfig); // Use static default
        }
      } catch (err) {
        console.error('Error loading Stripe settings:', err);
        setStripeError('Failed to load Stripe settings: ' + err.message);
      }
    };

    fetchStripeConfig();
  }, [isAdmin]); // Only depends on isAdmin

  // Validate Stripe form
  useEffect(() => {
    const isValid = Object.values(stripeConfig).every((value) => value.trim() !== '');
    setStripeFormValid(isValid);
  }, [stripeConfig]); // Correct dependency

  // Handle save action
  const handleSaveStripeSettings = async () => {
    if (!stripeFormValid) {
      setStripeError('Please fill out all required fields.');
      return;
    }
    setStripeSuccess('');
    setStripeError('');
    setIsSaving(true);
    try {
      await onSave(stripeConfig);
      setStripeSuccess('Stripe settings saved successfully!');
    } catch (err) {
      console.error('Error saving Stripe settings:', err);
      setStripeError('Failed to save Stripe settings: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Clear messages after a timeout
  useEffect(() => {
    if (stripeSuccess || stripeError) {
      const timer = setTimeout(() => {
        setStripeSuccess('');
        setStripeError('');
      }, 5000); // Clear after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [stripeSuccess, stripeError]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-300">
      {stripeSuccess && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
          {stripeSuccess}
        </div>
      )}
      {stripeError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {stripeError}
        </div>
      )}
      <h3 className="text-2xl font-bold mb-4 text-gray-600">Payment Settings (Stripe)</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-gray-600 mb-1">
            Publishable Key <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={stripeConfig.publishableKey}
            onChange={(e) => setStripeConfig({ ...stripeConfig, publishableKey: e.target.value })}
            className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="e.g., pk_test_..."
            required
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">
            Secret Key <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            value={stripeConfig.secretKey}
            onChange={(e) => setStripeConfig({ ...stripeConfig, secretKey: e.target.value })}
            className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="e.g., sk_test_..."
            required
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">
            Webhook Secret <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            value={stripeConfig.webhookSecret}
            onChange={(e) => setStripeConfig({ ...stripeConfig, webhookSecret: e.target.value })}
            className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="e.g., whsec_..."
            required
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleSaveStripeSettings}
            disabled={!stripeFormValid || isSaving}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors duration-300 ${
              stripeFormValid && !isSaving
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-400 text-gray-700 cursor-not-allowed'
            }`}
          >
            {isSaving ? 'Saving...' : 'Save Stripe Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripeConfig;